import formatMoney from '../../core/formatMoney';
import { selectNewFilter, getCurrentPage } from './filtering';
import { handleize } from '../../core/helpers';

/* IMPORTANT: Doesn't apply to the search overlay */

const previousState = () => {
  const saved_filters = JSON.parse(window.localStorage.getItem('wild-filter'));
  const previous_page = window.localStorage.getItem('previous-page') || '';
  const current_path = window.location.pathname + window.location.search;

  // Extract pathname + search from previous_page (handles full URLs and relative paths)
  let previous_full_path = previous_page;
  try {
    const url = new URL(previous_page, window.location.origin);
    previous_full_path = url.pathname + url.search;
  } catch (e) {
    // If previous_page is already a path, this will fail, so just use it as is
  }

  console.log('[wild-filter]', saved_filters, previous_page, current_path, previous_full_path);

  // Check if previous_page is a product page or matches the current collection page
  // Detect if the user navigated back using the browser's back button
  const isBackNavigation = window.performance && window.performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
  const isProductPage = previous_page.includes('/products/');
  const isSameCollectionPage = previous_full_path === current_path;

  console.log(`[wild-filter] isProductPage: ${isProductPage}, isSameCollectionPage: ${isSameCollectionPage}`);

  if ((isProductPage && isBackNavigation) || isSameCollectionPage) {
    console.log('[wild-filter] apply');
    // window.localStorage.removeItem('wild-filter');
    return {
      saved_filters,
      previous_page,
    };
  }

  // saved_filters === null
  console.log('[wild-filter] don\'t apply');
  window.localStorage.removeItem('wild-filter');
  return null;
};

/**
 * Get the current sorting state based on the current page and previous state.
 * 
 * @param {boolean} with_previous_state - If true, will return the previous state if available
 * 
 * @returns {string} The current sorting state
 */
const getSortingState = (with_previous_state = false) => {
  const previous_state = (with_previous_state) ? previousState() : null;

  const is_mobile = window.theme.breakpoints.current === 'mobile';
  const sorting_container = is_mobile ? document.querySelector('[data-mobile-filter-sorting]') : document.querySelector('[data-show-desktop-sort-nav]')?.parentElement?.querySelector('[data-sort-by]');
  const sortby_name = is_mobile ? 'sortby-mobile' : 'sortby';

  // Coming back from the PDP or refreshing the page
  if (previous_state !== null && previous_state?.previous_page.includes('/products/')) {
    return previous_state?.saved_filters?.sort_by || 'manual';
  }

  if (previous_state === null || !previous_state?.previous_page.includes('/products/')) {
    let sortby_value = '';
    const checked_input = sorting_container?.querySelector(`input[name="${sortby_name}"]:checked`);
    sortby_value = checked_input?.value || 'manual';

    // Assign the same sorting to desktop and mobile
    sorting_container?.querySelectorAll(`input[value="${sortby_value}"]`).forEach(input => {
      input.checked = true;
    });
    document.querySelectorAll(`[data-mobile-filter-sorting] input[value="${sortby_value}"]`).forEach(input => {
      input.checked = true;
    });

    window.theme.collections.sorting = sortby_value;
    return sortby_value;
  }

  // Assign the same sorting to desktop and mobile
  sorting_container?.querySelectorAll(`input[value="${saved_filters.sort_by}"]`).forEach(input => {
    input.checked = true;
  });
  document.querySelectorAll(`[data-mobile-filter-sorting] input[value="${saved_filters.sort_by}"]`).forEach(input => {
    input.checked = true;
  });

  window.theme.collections.sorting = saved_filters.sort_by;
  return saved_filters.sort_by;
};

// Save local storage everytime a filter is selected
const saveFilterState = () => {
  if (!document.querySelector('body').classList.contains('template-plp')) {
    return;
  }

  window.localStorage.removeItem('wild-filter');

  const filter_options = {
    page: getCurrentPage(),
    sort_by: '',
  };

  filter_options.sort_by = getSortingState(true);

  // Push the selected option dynamically (since we now have dynamic filters)
  document.querySelectorAll('[data-filter-options]').forEach(option => {
    const active_options = option.querySelectorAll('[data-clickable-option].active');

    if (active_options.length) {
      const group_name = option.getAttribute('data-filter-options-type');

      active_options.forEach(active_option => {
        if (typeof filter_options[group_name] === 'undefined') {
          filter_options[group_name] = [];
        }

        const add_value = active_option.getAttribute('data-option-value');
        // We can have duplicates since we have multiple filter components on the page
        if (!filter_options[group_name].includes(add_value)) {
          filter_options[group_name].push(add_value);
        }
      });
    }
  });

  window.localStorage.setItem('wild-filter', JSON.stringify(filter_options));
};

const getPaginationState = () => {
  const saved_filters = JSON.parse(window.localStorage.getItem('wild-filter'));

  if (saved_filters === null) {
    return 1;
  }
  
  return saved_filters.page;
};

/**
 * Apply the saved filters when coming back from the product page OR refreshing the page OR after selecting it from the sort by feature
 * 
 * @param {boolean} new_nosto_event - If true, will trigger a new Nosto event
 * 
 * @returns {Promise<boolean>} - Returns true if filters were applied, false otherwise
 */
const applyFilterState = async (new_nosto_event = true) => {
  const previous_filters = previousState();
  console.log('[wild-filter] applyFilterState', previous_filters);
  if (previous_filters === null) {
    return false;
  }

  // window.localStorage.removeItem('wild-filter');

  document.querySelectorAll('[data-selected-filters-list] [data-selected-filters-item]').forEach(item => item.remove());

  for (const saved_filter in previous_filters.saved_filters) {
    if (saved_filter === 'page' || saved_filter === 'sort_by') continue;

    const filterOptionsContainers = document.querySelectorAll(`[data-filter-options-type="${saved_filter}"]`);
    if (!filterOptionsContainers.length) continue;

    const saved_options = previous_filters.saved_filters[saved_filter];

    filterOptionsContainers.forEach(container => {
      const clickableOptions = container.querySelectorAll('[data-clickable-option]');
      clickableOptions.forEach(option => {
        option.classList.remove('active');
        option.parentElement?.classList.remove('active');

        const optionValue = option.getAttribute('data-option-value');
        if (Array.isArray(saved_options) && saved_options.includes(optionValue)) {
          option.classList.add('active');
          option.parentElement?.classList.add('active');

          let saved_option_value = optionValue;
          if (saved_option_value.includes('vt::')) {
            saved_option_value = saved_option_value.split('::').pop();
          }

          if (saved_filter === 'price') {
            const price_range_parts = saved_option_value.split('-');
            saved_option_value = `${formatMoney(Math.ceil(Number(price_range_parts[0]) * window.Shopify.currency.rate) * 100, theme.moneyFormat).replace('.00', '').replace(',00', '')}-${formatMoney(Math.ceil(Number(price_range_parts[1]) * window.Shopify.currency.rate) * 100, theme.moneyFormat).replace('.00', '').replace(',00', '')}`;
          }

          // Always add the selected filter item
          const selected_filter_item_html = `
            <div class="selected-filters__item"
              data-selected-filters-item
              data-selected-filters-short-value="${handleize(optionValue)}"
              data-selected-filters-type="${saved_filter}"
              data-selected-filters-value="${optionValue}"
              role="button"
              tabindex="0">
              ${saved_option_value}
            </div>
          `;

          const selected_filter_item_selector = `[data-selected-filters-type="${saved_filter}"][data-selected-filters-value="${optionValue}"]`;
          if (!document.querySelector(selected_filter_item_selector)) {
            const clearAllFilters = document.querySelectorAll('[data-selected-filters-list] [data-clear-all-filters]');
            if (clearAllFilters.length > 0) {
              clearAllFilters.forEach(el => {
                el.insertAdjacentHTML('beforebegin', selected_filter_item_html);
              });
            }
          }
        }
      });
    });
  }

  const filter_container = document.querySelector('[data-collection-grid-container] [data-selected-filters]');
  const has_selected_filters = (filter_container?.querySelectorAll('[data-selected-filters-item]').length > 0) ?? false;
  filter_container?.classList.toggle('active', has_selected_filters);

  // Apply the filters in Nosto
  if (new_nosto_event) {
    const saved_page = (typeof previous_filters?.saved_filters?.page === 'undefined') ? 1 : previous_filters?.saved_filters?.page;
    window.theme.collections.page = saved_page;
    const sorting_state = previous_filters?.saved_filters?.sort_by || getSortingState();
    // problem: we don't have the sorting when coming back from pdp
    window.theme.collections.sorting = sorting_state;

    /*
    {
        "page": 1,
        "sort_by": "manual"
    }
    
    {
      "saved_filters": {
      "page": 1,
      "sort_by": "manual",
      "skus.customFields.astr-color_family": [
        "black",
        "blue"
      ],
      "skus.customFields.size": [
        "M"
      ]
    }
    */
    const filterKeys = Object.keys(previous_filters?.saved_filters || {}).filter(
      key => key !== 'page' && key !== 'sort_by'
    );
    
    console.log('saved_page', saved_page, 'sorting_state', sorting_state, 'previous_filters?.saved_filters', previous_filters?.saved_filters);
    if (saved_page === 1 && sorting_state === 'manual' && filterKeys.length === 0) {
      // If we are on the first page and sorting is manual with no filters applied, we don't need to load the collection again
      console.log('[wild-filter] No need to load collection again');
      return true;
    }

    if (saved_page > 1) {
      await selectNewFilter(false, sorting_state, saved_page);
    } else {
      await selectNewFilter(false, sorting_state);
    }
  }

  return true;
};

export {
  saveFilterState,
  applyFilterState,
  getSortingState,
  getPaginationState,
  previousState,
};
